import React, { useEffect, useState } from 'react';
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useNavigate,
} from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HelpIcon from '@mui/icons-material/Help'; // Import HelpIcon
import { styled } from '@mui/system';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import Home from './Home';
import MapPage from './MapPage';
import ActiveConversation from './ActiveConversation';
import PreviousOrders from './PreviousOrders';
import AvailableValets from './AvailableValets';
import EditProfile from './EditProfile';
import WaitingForValet from './WaitingForValet';
import SignIn from './SignIn'; // Import the SignIn component
import GuestForm from './GuestForm'; // Import the GuestForm component
import PaymentResult from './PaymentResult';
import ErrorBoundary from './ErrorBoundary'; // Import the ErrorBoundary component
import AdminDashboard from './AdminDashboard';

// Add debug logging
console.log('App.js loading...');

const Root = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    height: '100vh', // Use exact viewport height
    backgroundColor: '#F8FAFC',
    overflow: 'hidden', // Prevent scrolling on the root element
});

const StyledAppBar = styled(AppBar)({
    background: '#113524', // Solid dark green background
    boxShadow: 'none',
    borderBottom: 'none', // Remove border
});

const Title = styled(Typography)({
    color: '#fff',
    fontSize: '1.25rem',
    fontWeight: 500,
    fontFamily: '"Poppins", sans-serif', // Modern font
    marginLeft: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
        opacity: 0.9,
    },
});

const ContentContainer = styled(Container)(({ theme }) => ({
    flex: 1, // Take up remaining space
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    paddingTop: theme.spacing(8), // Add padding top to account for AppBar height
    overflow: 'auto',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(2),
        paddingTop: theme.spacing(10), // Adjust padding top for larger screens
    },
}));

const Footer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    borderTop: '1px solid #ccc',
    textAlign: 'center',
}));

// Separate component for handling root path redirect
const RootRedirect = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                navigate('/home');
            } else {
                navigate('/signin'); // Redirect to sign-in page if not authenticated
            }
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}>
                <CircularProgress sx={{ color: '#113524' }} />
            </Box>
        );
    }

    return null; // Avoid rendering any content for `/` if no user is found.
};

const AuthCheck = ({ children }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (!user) {
                navigate('/signin'); // Redirect to sign-in page if not authenticated
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100vh - 64px)',
                }}>
                <CircularProgress sx={{ color: '#113524' }} />
            </Box>
        );
    }

    return <>{children}</>;
};

// Navigation handler component for the title
const TitleLink = () => {
    const navigate = useNavigate();

    const handleTitleClick = () => {
        const currentPath = window.location.pathname;
        if (currentPath !== '/home') {
            navigate('/home');
        }
    };

    return (
        <Title component='div' onClick={handleTitleClick}>
            Valet NYC
        </Title>
    );
};

// Function to handle the help button click
const handleHelpClick = () => {
    const phoneNumber = '9796616772';
    const iMessageUrl = `sms:${phoneNumber}`; // URL scheme for iMessage
    window.location.href = iMessageUrl; // Redirect to iMessage
};

// Main app chrome (AppBar + footer). Admin routes render outside this layout.
function MainLayout() {
    return (
        <Root>
            <StyledAppBar>
                <Toolbar>
                    <IconButton edge='start' color='inherit' sx={{ mr: 2 }}>
                        <MenuIcon sx={{ fontSize: '1.5rem' }} />
                    </IconButton>
                    <TitleLink />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}>
                        <Typography
                            variant='body2'
                            sx={{
                                color: '#fff',
                                cursor: 'pointer',
                                fontWeight: 500,
                            }}
                            onClick={handleHelpClick}>
                            Need help?
                        </Typography>
                        <IconButton
                            color='inherit'
                            onClick={handleHelpClick}
                            sx={{ padding: 0 }}
                        >
                            <HelpIcon sx={{ fontSize: '1.5rem' }} />
                        </IconButton>
                    </Box>
                </Toolbar>
            </StyledAppBar>

            <ContentContainer maxWidth={false}>
                <Routes>
                    <Route path='/' element={<RootRedirect />} />
                    <Route path='/signin' element={<SignIn />} />
                    <Route path='/guest-form' element={<GuestForm />} />
                    <Route path='/available-valets' element={<AvailableValets />} />
                    <Route path='/active-chat' element={<ActiveConversation />} />
                    <Route path='/waiting-for-valet' element={<WaitingForValet />} />
                    <Route path='/payment-result' element={<PaymentResult />} />
                    <Route
                        path='/*'
                        element={
                            <AuthCheck>
                                <Routes>
                                    <Route path='/home' element={<Home />} />
                                    <Route path='/map' element={<MapPage />} />
                                    <Route path='/previous-orders' element={<PreviousOrders />} />
                                    <Route path='/edit-profile' element={<EditProfile />} />
                                </Routes>
                            </AuthCheck>
                        }
                    />
                </Routes>
            </ContentContainer>

            <Footer>
                <Typography variant='body2' color='textSecondary' align='center'>
                    © 2024 Valet NYC. All rights reserved.
                </Typography>
                <Typography variant='body2' color='textSecondary' align='center'>
                    Based in New York, NY.
                </Typography>
            </Footer>
        </Root>
    );
}

function App() {
    console.log('App component rendering...');

    return (
        <ErrorBoundary>
            <Router>
                <Routes>
                    {/* TODO: add admin auth guard */}
                    <Route path='/admin/dashboard' element={<AdminDashboard />} />
                    <Route path='/*' element={<MainLayout />} />
                </Routes>
            </Router>
        </ErrorBoundary>
    );
}

export default App;
